import { useEffect, useMemo, useState } from "react"
import { useNavigate, Routes, Route, Navigate } from "react-router-dom"
import { AppSidebar } from "@/components/app-sidebar"
import { SiteHeader } from "@/components/site-header"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card"
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar"
import { logoutUser, getSession, getToken } from "@/lib/auth"
import {
  crearSolicitudBeneficio,
  obtenerFormularioBeneficio,
  obtenerTiposBeneficio,
  obtenerHistorialSolicitudes,
  actualizarDocumentosSolicitud,
  type CampoDinamico,
  type TipoBeneficio,
} from "@/lib/module2Api"
import { getRequestTrackingDetails, type TrackingDetail } from "@/lib/trackingApi"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { FileEditIcon, XIcon, ActivityIcon, CheckCircle2Icon, ClockIcon, SearchIcon, PlayIcon } from "lucide-react"

type ResultadoSolicitud = {
  mensaje: string
  tracking_id: string
  estado: string
} | null

function DashboardNewRequest({ tiposBeneficio, cargandoTipos, fullName, email, identificacion }: any) {
  const [beneficioSeleccionado, setBeneficioSeleccionado] = useState<TipoBeneficio | null>(null)
  const [camposDinamicos, setCamposDinamicos] = useState<CampoDinamico[]>([])
  const [valoresCamposDinamicos, setValoresCamposDinamicos] = useState<Record<string, string>>({})
  const [descripcionSolicitud, setDescripcionSolicitud] = useState("")
  const [archivosPorDocumento, setArchivosPorDocumento] = useState<Record<string, File>>({})
  const [resultadoSolicitud, setResultadoSolicitud] = useState<ResultadoSolicitud>(null)
  const [error, setError] = useState("")
  const [enviando, setEnviando] = useState(false)

  async function manejarSeleccionBeneficio(idBeneficio: string) {
    setError("")
    setResultadoSolicitud(null)
    const selectedBenefit = tiposBeneficio.find((item: any) => String(item.id) === idBeneficio)
    if (!selectedBenefit) {
      setBeneficioSeleccionado(null)
      setCamposDinamicos([])
      setValoresCamposDinamicos({})
      setArchivosPorDocumento({})
      return
    }

    try {
      const token = getToken() || ""
      const schemaResponse = await obtenerFormularioBeneficio(selectedBenefit.id, token)
      setBeneficioSeleccionado(selectedBenefit)
      setCamposDinamicos(schemaResponse.campos)
      setValoresCamposDinamicos({})
      setArchivosPorDocumento({})
    } catch (schemaError) {
      setError(schemaError instanceof Error ? schemaError.message : "No se pudo cargar el formulario.")
    }
  }

  function manejarCambioCampoDinamico(key: string, value: string) {
    setValoresCamposDinamicos((current) => ({
      ...current,
      [key]: value,
    }))
  }

  function manejarCambioArchivoEspecifico(documentoRequerido: string, event: React.ChangeEvent<HTMLInputElement>) {
    setError("")
    const selectedFile = event.target.files?.[0]

    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        setError(`El archivo ${selectedFile.name} excede el limite de 5MB.`)
        event.target.value = ""
        return
      }
      const allowedTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg"]
      if (!allowedTypes.includes(selectedFile.type)) {
        setError(`El formato del archivo ${selectedFile.name} no es valido. Solo PDF, PNG y JPEG.`)
        event.target.value = ""
        return
      }
    }

    setArchivosPorDocumento((current) => {
      const updated = { ...current }
      if (selectedFile) {
        const renamedFile = new File([selectedFile], `${documentoRequerido.replace(/\//g, "-")}_${selectedFile.name}`, { type: selectedFile.type })
        updated[documentoRequerido] = renamedFile
      } else {
        delete updated[documentoRequerido]
      }
      return updated
    })
  }

  const faltanCamposDinamicos = useMemo(() => {
    return camposDinamicos
      .filter((field) => field.required)
      .some((field) => !(valoresCamposDinamicos[field.key] ?? "").trim())
  }, [camposDinamicos, valoresCamposDinamicos])

  const faltanArchivos = useMemo(() => {
    if (!beneficioSeleccionado) return false
    return beneficioSeleccionado.documentos_requeridos.some(
      (doc) => !archivosPorDocumento[doc]
    )
  }, [beneficioSeleccionado, archivosPorDocumento])

  async function manejarEnvio(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError("")
    setResultadoSolicitud(null)

    if (!beneficioSeleccionado) {
      setError("Selecciona un tipo de prestación antes de enviar.")
      return
    }

    if (!descripcionSolicitud.trim()) {
      setError("Debes describir la solicitud.")
      return
    }

    if (faltanCamposDinamicos) {
      setError("Completa todos los campos dinámicos obligatorios.")
      return
    }

    if (faltanArchivos) {
      setError("Debes adjuntar todos los documentos requeridos de forma separada.")
      return
    }

    const archivosAEnviar = Object.values(archivosPorDocumento)

    try {
      setEnviando(true)
      const token = getToken()
      if (!token) throw new Error("No estás autenticado.")
      
      const response = await crearSolicitudBeneficio({
        nombreCompleto: fullName,
        email,
        identificacion,
        numeroSeguridadSocial: identificacion,
        beneficioId: beneficioSeleccionado.id,
        descripcionSolicitud,
        camposDinamicos: valoresCamposDinamicos,
        archivos: archivosAEnviar,
      }, token)
      setResultadoSolicitud(response)
      setDescripcionSolicitud("")
      setArchivosPorDocumento({})
      setValoresCamposDinamicos({})
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "No se pudo enviar la solicitud.")
    } finally {
      setEnviando(false)
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Nueva solicitud de prestación</CardTitle>
          <CardDescription>
            Selecciona un beneficio, completa el formulario dinámico y adjunta los documentos requeridos de forma separada (PDF/PNG/JPEG, máx. 5MB).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <Field>
              <FieldLabel>Nombre completo</FieldLabel>
              <Input value={fullName} disabled />
            </Field>
            <Field>
              <FieldLabel>Correo electrónico</FieldLabel>
              <Input value={email} disabled />
            </Field>
            <Field>
              <FieldLabel>Identificación / NSS</FieldLabel>
              <Input value={identificacion} disabled />
            </Field>
          </div>
        </CardContent>
      </Card>

      <form className="space-y-6" onSubmit={manejarEnvio}>
        <Card id="solicitud-beneficio">
          <CardHeader>
            <CardTitle>Selección del tipo de beneficio</CardTitle>
            <CardDescription>El sistema carga los campos según la prestación elegida.</CardDescription>
          </CardHeader>
          <CardContent>
            <FieldGroup>
              <Field>
                <FieldLabel>Tipo de prestación</FieldLabel>
                <Select value={beneficioSeleccionado ? String(beneficioSeleccionado.id) : ""} onValueChange={manejarSeleccionBeneficio}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={cargandoTipos ? "Cargando beneficios..." : "Selecciona un beneficio"} />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposBeneficio.map((beneficio: any) => (
                      <SelectItem key={beneficio.id} value={String(beneficio.id)}>
                        {beneficio.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {beneficioSeleccionado ? (
                <>
                  <FieldDescription>{beneficioSeleccionado.descripcion}</FieldDescription>
                  <div id="requisitos" className="rounded-lg border p-3 text-sm">
                    <strong>Requisitos y documentos requeridos:</strong>
                    <ul className="mt-2 list-inside list-disc space-y-1">
                      {beneficioSeleccionado.documentos_requeridos.map((documento: string) => (
                        <li key={documento}>{documento}</li>
                      ))}
                    </ul>
                    <p className="mt-2 text-muted-foreground">
                      Tiempo estimado de procesamiento: {beneficioSeleccionado.dias_procesamiento} días
                    </p>
                  </div>
                </>
              ) : null}
            </FieldGroup>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Formulario dinámico</CardTitle>
            <CardDescription>Completa la información requerida (solicitante, beneficiario o cotizante según proceda).</CardDescription>
          </CardHeader>
          <CardContent>
            <FieldGroup>
              {camposDinamicos.length === 0 ? (
                <FieldDescription>Selecciona un beneficio para mostrar los campos.</FieldDescription>
              ) : (
                camposDinamicos.map((campo) => (
                  <Field key={campo.key}>
                    <FieldLabel htmlFor={campo.key}>
                      {campo.label}
                      {campo.required ? " *" : ""}
                    </FieldLabel>
                    {campo.type === "textarea" ? (
                      <textarea
                        id={campo.key}
                        className="min-h-28 w-full rounded-lg border border-input bg-transparent px-2.5 py-2 text-sm focus-visible:border-ring focus-visible:outline-none"
                        value={valoresCamposDinamicos[campo.key] ?? ""}
                        onChange={(event) => manejarCambioCampoDinamico(campo.key, event.target.value)}
                        required={campo.required}
                      />
                    ) : null}
                    {campo.type === "select" ? (
                      <Select
                        value={valoresCamposDinamicos[campo.key] ?? ""}
                        onValueChange={(value) => manejarCambioCampoDinamico(campo.key, value)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Selecciona una opción" />
                        </SelectTrigger>
                        <SelectContent>
                          {(campo.options ?? []).map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : null}
                    {campo.type !== "textarea" && campo.type !== "select" ? (
                      <Input
                        id={campo.key}
                        type={campo.type === "number" ? "number" : campo.type}
                        value={valoresCamposDinamicos[campo.key] ?? ""}
                        onChange={(event) => manejarCambioCampoDinamico(campo.key, event.target.value)}
                        required={campo.required}
                      />
                    ) : null}
                  </Field>
                ))
              )}
              <Field>
                <FieldLabel htmlFor="descripcionSolicitud">Descripción de la solicitud *</FieldLabel>
                <textarea
                  id="descripcionSolicitud"
                  className="min-h-32 w-full rounded-lg border border-input bg-transparent px-2.5 py-2 text-sm focus-visible:border-ring focus-visible:outline-none"
                  value={descripcionSolicitud}
                  onChange={(event) => setDescripcionSolicitud(event.target.value)}
                  required
                />
              </Field>
            </FieldGroup>
          </CardContent>
        </Card>

        <Card id="documentos-subidos">
          <CardHeader>
            <CardTitle>Carga de archivos adjuntos</CardTitle>
            <CardDescription>Sube cada documento requerido de forma separada según los requisitos de la prestación.</CardDescription>
          </CardHeader>
          <CardContent>
            <FieldGroup>
              {!beneficioSeleccionado ? (
                <FieldDescription>Selecciona un beneficio primero para ver la lista de documentos a subir.</FieldDescription>
              ) : beneficioSeleccionado.documentos_requeridos.length === 0 ? (
                <FieldDescription>Esta prestación no requiere documentos adjuntos adicionales.</FieldDescription>
              ) : (
                beneficioSeleccionado.documentos_requeridos.map((documento: string) => (
                  <Field key={documento}>
                    <FieldLabel htmlFor={`file-${documento}`}>Documento: {documento} *</FieldLabel>
                    <Input 
                      id={`file-${documento}`} 
                      type="file" 
                      accept=".pdf,image/png,image/jpeg" 
                      onChange={(e) => manejarCambioArchivoEspecifico(documento, e)} 
                      required
                    />
                    {archivosPorDocumento[documento] && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Archivo cargado: {archivosPorDocumento[documento].name} ({Math.ceil(archivosPorDocumento[documento].size / 1024)} KB)
                      </p>
                    )}
                  </Field>
                ))
              )}
            </FieldGroup>
          </CardContent>
        </Card>

        <Card id="mis-solicitudes">
          <CardHeader>
            <CardTitle>Envío de solicitud</CardTitle>
            <CardDescription>Al enviar, se generará un número de seguimiento único.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            {resultadoSolicitud ? (
              <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 text-sm">
                <p className="font-medium">{resultadoSolicitud.mensaje}</p>
                <p className="mt-2">
                  <strong>ID de Seguimiento:</strong> {resultadoSolicitud.tracking_id}
                </p>
                <p>
                  <strong>Estado inicial:</strong> {resultadoSolicitud.estado}
                </p>
              </div>
            ) : null}
            <Button type="submit" disabled={enviando}>
              {enviando ? "Enviando solicitud..." : "Enviar solicitud"}
            </Button>
          </CardContent>
        </Card>
      </form>
    </>
  )
}

function DashboardHistory({ tiposBeneficio }: { tiposBeneficio: TipoBeneficio[] }) {
  const [historial, setHistorial] = useState<any[]>([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState("")

  // Update modal state
  const [requestAActualizar, setRequestAActualizar] = useState<any>(null)
  const [archivosAActualizar, setArchivosAActualizar] = useState<Record<string, File>>({})
  const [actualizando, setActualizando] = useState(false)
  const [errorActualizacion, setErrorActualizacion] = useState("")

  // Tracking modal state
  const [trackingRequest, setTrackingRequest] = useState<number | null>(null)
  const [trackingDetail, setTrackingDetail] = useState<TrackingDetail | null>(null)
  const [cargandoTracking, setCargandoTracking] = useState(false)
  const [errorTracking, setErrorTracking] = useState("")

  useEffect(() => {
    let mounted = true
    async function loadHistory() {
      try {
        setCargando(true)
        const token = getToken()
        if (!token) throw new Error("No estás autenticado.")
        const data = await obtenerHistorialSolicitudes(token)
        if (mounted) {
          setHistorial(data)
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : "Error al cargar historial o no existen transacciones previas en la base de datos.")
        }
      } finally {
        if (mounted) setCargando(false)
      }
    }
    loadHistory()
    return () => { mounted = false }
  }, [])

  function abrirModalActualizacion(req: any) {
    setRequestAActualizar(req)
    setArchivosAActualizar({})
    setErrorActualizacion("")
  }

  function cerrarModalActualizacion() {
    setRequestAActualizar(null)
    setArchivosAActualizar({})
    setErrorActualizacion("")
  }

  function manejarCambioArchivoActualizacion(documentoRequerido: string, event: React.ChangeEvent<HTMLInputElement>) {
    setErrorActualizacion("")
    const selectedFile = event.target.files?.[0]

    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        setErrorActualizacion(`El archivo ${selectedFile.name} excede el limite de 5MB.`)
        event.target.value = ""
        return
      }
      const allowedTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg"]
      if (!allowedTypes.includes(selectedFile.type)) {
        setErrorActualizacion(`El formato del archivo ${selectedFile.name} no es valido. Solo PDF, PNG y JPEG.`)
        event.target.value = ""
        return
      }
    }

    setArchivosAActualizar((current) => {
      const updated = { ...current }
      if (selectedFile) {
        const renamedFile = new File([selectedFile], `${documentoRequerido.replace(/\//g, "-")}_${selectedFile.name}`, { type: selectedFile.type })
        updated[documentoRequerido] = renamedFile
      } else {
        delete updated[documentoRequerido]
      }
      return updated
    })
  }

  async function enviarActualizacionDocumentos() {
    const archivos = Object.values(archivosAActualizar)
    if (archivos.length === 0) {
      setErrorActualizacion("Debes seleccionar al menos un documento para actualizar.")
      return
    }

    try {
      setActualizando(true)
      setErrorActualizacion("")
      const token = getToken()
      if (!token) throw new Error("No autenticado")

      await actualizarDocumentosSolicitud(token, requestAActualizar.id, archivos)

      // Reload history to see updated status
      const data = await obtenerHistorialSolicitudes(token)
      setHistorial(data)

      cerrarModalActualizacion()
    } catch (err) {
      setErrorActualizacion(err instanceof Error ? err.message : "Hubo un error al actualizar los documentos.")
    } finally {
      setActualizando(false)
    }
  }

  const beneficioDelModal = useMemo(() => {
    if (!requestAActualizar) return null
    return tiposBeneficio.find(b => b.id === requestAActualizar.beneficio_id)
  }, [requestAActualizar, tiposBeneficio])

  async function abrirTracking(requestId: number) {
    setTrackingRequest(requestId)
    setTrackingDetail(null)
    setErrorTracking("")
    setCargandoTracking(true)
    
    try {
      const token = getToken()
      if (!token) throw new Error("No estás autenticado.")
      const data = await getRequestTrackingDetails(token, requestId)
      setTrackingDetail(data)
    } catch (err) {
      setErrorTracking(err instanceof Error ? err.message : "No se pudo cargar el seguimiento.")
    } finally {
      setCargandoTracking(false)
    }
  }

  function cerrarTracking() {
    setTrackingRequest(null)
    setTrackingDetail(null)
    setErrorTracking("")
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Mis Solicitudes</CardTitle>
          <CardDescription>Lista de todas las solicitudes de prestaciones sociales realizadas previamente.</CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : cargando ? (
            <p className="text-sm text-muted-foreground">Cargando solicitudes...</p>
          ) : historial.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-lg font-medium text-muted-foreground">No existen solicitudes previas.</p>
              <p className="text-sm text-muted-foreground mt-1">Aún no has solicitado ninguna prestación social.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID Seguimiento</TableHead>
                  <TableHead>Prestación</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historial.map((req) => (
                  <TableRow key={req.id}>
                    <TableCell className="font-medium">{req.tracking_id}</TableCell>
                    <TableCell>{req.beneficio}</TableCell>
                    <TableCell>{new Date(req.fecha_solicitud).toLocaleDateString("es-GQ")}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${req.estado === 'Documentos Actualizados' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-800'}`}>
                        {req.estado}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => abrirTracking(req.id)} className="gap-2">
                          <ActivityIcon className="size-4" />
                          Ver Seguimiento
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => abrirModalActualizacion(req)} className="gap-2">
                          <FileEditIcon className="size-4" />
                          Actualizar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Actualizar Documentos Modal */}
      {requestAActualizar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-lg bg-background shadow-xl">
            <CardHeader className="relative">
              <Button 
                variant="ghost" 
                size="icon" 
                className="absolute right-4 top-4" 
                onClick={cerrarModalActualizacion}
              >
                <XIcon className="size-4" />
              </Button>
              <CardTitle>Actualizar Documentos</CardTitle>
              <CardDescription>
                Solicitud: <strong>{requestAActualizar.tracking_id}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent>
              {errorActualizacion && (
                <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {errorActualizacion}
                </div>
              )}
              
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Selecciona nuevos archivos <strong>solo</strong> para los documentos que deseas reemplazar. Los demás permanecerán intactos.
                </p>
                
                <FieldGroup>
                  {!beneficioDelModal ? (
                    <FieldDescription>No se encontró información de requisitos para esta prestación.</FieldDescription>
                  ) : beneficioDelModal.documentos_requeridos.length === 0 ? (
                    <FieldDescription>Esta prestación no requería documentos adjuntos.</FieldDescription>
                  ) : (
                    beneficioDelModal.documentos_requeridos.map((documento: string) => (
                      <Field key={documento}>
                        <FieldLabel htmlFor={`update-file-${documento}`}>Reemplazar: {documento}</FieldLabel>
                        <Input 
                          id={`update-file-${documento}`} 
                          type="file" 
                          accept=".pdf,image/png,image/jpeg" 
                          onChange={(e) => manejarCambioArchivoActualizacion(documento, e)} 
                        />
                        {archivosAActualizar[documento] && (
                          <p className="text-xs text-primary mt-1">
                            Nuevo archivo seleccionado: {archivosAActualizar[documento].name}
                          </p>
                        )}
                      </Field>
                    ))
                  )}
                </FieldGroup>
              </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-2 border-t pt-4">
              <Button variant="outline" onClick={cerrarModalActualizacion} disabled={actualizando}>
                Cancelar
              </Button>
              <Button onClick={enviarActualizacionDocumentos} disabled={actualizando || Object.keys(archivosAActualizar).length === 0}>
                {actualizando ? "Subiendo..." : "Subir Actualizaciones"}
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}

      {/* Tracking Modal */}
      {trackingRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-2xl bg-background shadow-xl">
            <CardHeader className="relative">
              <Button 
                variant="ghost" 
                size="icon" 
                className="absolute right-4 top-4" 
                onClick={cerrarTracking}
              >
                <XIcon className="size-4" />
              </Button>
              <CardTitle>Seguimiento de Solicitud</CardTitle>
              <CardDescription>Visualiza el estado y progreso de tu solicitud.</CardDescription>
            </CardHeader>
            <CardContent>
              {errorTracking ? (
                <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {errorTracking}
                </div>
              ) : cargandoTracking ? (
                <p className="text-sm text-muted-foreground py-10 text-center">Cargando detalles...</p>
              ) : trackingDetail ? (
                <div className="space-y-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold">{trackingDetail.beneficio}</h3>
                      <p className="text-sm text-muted-foreground">ID: {trackingDetail.tracking_id}</p>
                    </div>
                    <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                      {trackingDetail.estado}
                    </span>
                  </div>

                  {/* Timeline */}
                  <div className="relative border-l-2 border-muted ml-3 space-y-8 pb-4">
                    {/* Step 1: Submitted */}
                    <div className="relative flex items-center">
                      <div className="absolute -left-[17px] flex size-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <CheckCircle2Icon className="size-5" />
                      </div>
                      <div className="ml-8">
                        <p className="font-medium">Enviado</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(trackingDetail.fecha_solicitud).toLocaleString("es-GQ")}
                        </p>
                      </div>
                    </div>

                    {/* Step 2: Under Review */}
                    <div className="relative flex items-center">
                      <div className={`absolute -left-[17px] flex size-8 items-center justify-center rounded-full ${["En Revisión", "Verificado", "Resuelto - Aprobado", "Resuelto - Denegado"].includes(trackingDetail.estado) ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                        {["En Revisión", "Verificado", "Resuelto - Aprobado", "Resuelto - Denegado"].includes(trackingDetail.estado) ? <CheckCircle2Icon className="size-5" /> : <SearchIcon className="size-4" />}
                      </div>
                      <div className="ml-8">
                        <p className={`font-medium ${["En Revisión", "Verificado", "Resuelto - Aprobado", "Resuelto - Denegado"].includes(trackingDetail.estado) ? '' : 'text-muted-foreground'}`}>En Revisión</p>
                        <p className="text-sm text-muted-foreground">
                          Análisis de documentos y perfil.
                        </p>
                      </div>
                    </div>

                    {/* Step 3: Verified */}
                    <div className="relative flex items-center">
                      <div className={`absolute -left-[17px] flex size-8 items-center justify-center rounded-full ${["Verificado", "Resuelto - Aprobado", "Resuelto - Denegado"].includes(trackingDetail.estado) ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                        {["Verificado", "Resuelto - Aprobado", "Resuelto - Denegado"].includes(trackingDetail.estado) ? <CheckCircle2Icon className="size-5" /> : <PlayIcon className="size-4" />}
                      </div>
                      <div className="ml-8">
                        <p className={`font-medium ${["Verificado", "Resuelto - Aprobado", "Resuelto - Denegado"].includes(trackingDetail.estado) ? '' : 'text-muted-foreground'}`}>Verificado</p>
                        <p className="text-sm text-muted-foreground">
                          Cotizaciones e historial validado.
                        </p>
                      </div>
                    </div>

                    {/* Step 4: Resolved */}
                    <div className="relative flex items-start">
                      <div className={`absolute -left-[17px] flex size-8 items-center justify-center rounded-full ${trackingDetail.estado.startsWith("Resuelto") ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                        {trackingDetail.estado.startsWith("Resuelto") ? <CheckCircle2Icon className="size-5" /> : <ClockIcon className="size-4" />}
                      </div>
                      <div className="ml-8">
                        <p className={`font-medium ${trackingDetail.estado.startsWith("Resuelto") ? '' : 'text-muted-foreground'}`}>Resuelto</p>
                        {trackingDetail.resolucion ? (
                          <div className="mt-2 rounded-lg border bg-muted/50 p-4 text-sm">
                            <p><strong>Decisión:</strong> <span className={trackingDetail.resolucion.decision === 'Aprobado' ? 'text-green-600' : 'text-red-600'}>{trackingDetail.resolucion.decision}</span></p>
                            <p className="mt-1"><strong>Justificación:</strong> {trackingDetail.resolucion.justificacion}</p>
                            <p className="mt-1 text-muted-foreground"><strong>Fecha:</strong> {new Date(trackingDetail.resolucion.fecha).toLocaleString("es-GQ")}</p>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">Esperando resolución final.</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-semibold text-sm mb-2">Archivos Adjuntos</h4>
                    <ul className="space-y-1">
                      {trackingDetail.archivos.map((file, idx) => (
                        <li key={idx} className="text-sm text-muted-foreground">
                          • {file.file_name} ({Math.ceil(Number(file.file_size) || 0)} KB) - Subido el {new Date(file.upload_date).toLocaleDateString("es-GQ")}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  )
}

export function ModuleTwoAffiliateDashboard() {
  const navigate = useNavigate()
  const session = getSession()

  const [tiposBeneficio, setTiposBeneficio] = useState<TipoBeneficio[]>([])
  const [cargandoTipos, setCargandoTipos] = useState(true)
  const [errorGlobal, setErrorGlobal] = useState("")

  const fullName = session?.fullName ?? ""
  const email = session?.email ?? ""
  const identificacion = session?.dniOrSsn ?? ""

  useEffect(() => {
    let mounted = true
    async function loadBenefitTypes() {
      try {
        setCargandoTipos(true)
        const token = getToken()
        if (!token) throw new Error("No token")
        const types = await obtenerTiposBeneficio(token)
        if (!mounted) return
        setTiposBeneficio(types)
      } catch (loadError) {
        if (!mounted) return
        setErrorGlobal("Error: No se pudo acceder a la información de requisitos o beneficios en la base de datos.")
      } finally {
        if (mounted) {
          setCargandoTipos(false)
        }
      }
    }
    loadBenefitTypes()
    return () => {
      mounted = false
    }
  }, [])

  function cerrarSesion() {
    logoutUser()
    navigate("/login", { replace: true })
  }

  return (
    <SidebarProvider>
      <AppSidebar
        user={{
          name: fullName || "Afiliado",
          email: email || "sin-correo@inseso.gq",
        }}
        onLogout={cerrarSesion}
      />
      <SidebarInset>
        <SiteHeader title="Portal de Prestaciones INSESO" />
        <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
          {errorGlobal ? (
             <Card>
               <CardContent className="pt-6">
                 <p className="text-destructive font-medium">{errorGlobal}</p>
                 <p className="text-sm mt-2">Le notificamos que no existen registros en la base de datos o hubo un error al conectar.</p>
               </CardContent>
             </Card>
          ) : (
            <Routes>
              <Route index element={<Navigate to="new" replace />} />
              <Route path="new" element={<DashboardNewRequest tiposBeneficio={tiposBeneficio} cargandoTipos={cargandoTipos} fullName={fullName} email={email} identificacion={identificacion} />} />
              <Route path="history" element={<DashboardHistory tiposBeneficio={tiposBeneficio} />} />
              <Route path="*" element={<Navigate to="new" replace />} />
            </Routes>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
